/**
 * Which language the app starts in, and how it follows the profile.
 *
 * Order: cached explicit choice → profile `users.locale` (if chosen) →
 * browser language (nearest supported, `detectBrowserLocale`) → English.
 *
 * - Cold start: the cached explicit choice (localStorage `pj-locale`, written
 *   only by a hand-picked language or a chosen profile value), else the
 *   browser language. Its chunk loads before the first paint — no English flash.
 * - Signed in (`syncProfileLocale` after `/api/me`):
 *   - a language picked by hand on a pre-auth page ("pending") is saved TO the
 *     profile;
 *   - a chosen profile language wins across devices and is cached;
 *   - no profile language (`null` — nobody chose one): a choice cached on this
 *     device is saved to the profile; otherwise the browser language is used,
 *     every time, without caching it, and reported as `detectedLocale` (server
 *     copy only — push / toasts — never a choice).
 * - Sign-up sends a hand-picked language as `locale` and the page language as
 *   `detectedLocale`.
 */
import { DEFAULT_LOCALE, INTL_TAG, detectBrowserLocale, isLocale, type Locale } from './config';
import { getLocale, setLocale, storedLocale } from './store';

const PENDING_KEY = 'pj-locale-pending';

export function initialLocale(): Locale {
  return storedLocale() ?? detectBrowserLocale();
}

/** Resolve and load the start-up locale. Never rejects. */
export async function initLocale(): Promise<void> {
  const locale = initialLocale();
  if (typeof document !== 'undefined') document.documentElement.lang = INTL_TAG[DEFAULT_LOCALE];
  const ok = await setLocale(locale, { persist: false });
  if (!ok && typeof document !== 'undefined') document.documentElement.lang = INTL_TAG[getLocale()];
}

/** A pre-auth pick: apply now and remember to save it to the profile after sign-in. */
export async function chooseLocaleBeforeSignIn(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(PENDING_KEY, locale);
  } catch {
    /* ignore */
  }
  await setLocale(locale);
}

function takePending(): Locale | null {
  try {
    const v = localStorage.getItem(PENDING_KEY);
    localStorage.removeItem(PENDING_KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

/** What to write back to the profile after reconciling, if anything. */
export type ProfileLocalePatch = { locale: Locale } | { detectedLocale: Locale } | null;

/**
 * Reconcile the UI language with the profile after `/api/me` and return the
 * profile update to send (an explicit choice, or the detected language), or null.
 */
export function syncProfileLocale(
  profileLocale: string | null | undefined,
  profileDetected?: string | null | undefined,
  browser: Locale = detectBrowserLocale(),
): ProfileLocalePatch {
  const pending = takePending();
  if (pending) {
    void setLocale(pending);
    return pending !== profileLocale ? { locale: pending } : null;
  }
  if (isLocale(profileLocale)) {
    void setLocale(profileLocale);
    return null;
  }
  // Nothing chosen on the profile.
  const cached = storedLocale();
  if (cached) {
    void setLocale(cached);
    return { locale: cached };
  }
  void setLocale(browser, { persist: false });
  return browser !== profileDetected ? { detectedLocale: browser } : null;
}

/** Sign-up body fields: a hand-picked language is a choice, the page language only detected. */
export function signupLocaleFields(): { locale?: Locale; detectedLocale: Locale } {
  const chosen = storedLocale();
  return { ...(chosen ? { locale: chosen } : {}), detectedLocale: getLocale() };
}
